import { Component, inject, Input } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';
import { getPasswordStrength, PasswordStrength } from '../../validators/password.validators';
import { PasswordPolicyService } from '../../../core/auth/services/password-policy.service';

@Component({
  selector: 'app-password-strength',
  standalone: true,
  imports: [],
  templateUrl: './password-strength.component.html',
  styleUrl: './password-strength.component.css'
})
export class PasswordStrengthComponent {
  @Input() password = '';
  @Input() showText = true;
  @Input() showIndicator = true;

  private readonly _translocoService = inject(TranslocoService);
  private readonly _passwordPolicyService = inject(PasswordPolicyService);

  /**
   * Calculates and returns the strength of the current password.
   * Uses cached API policy when available (5B); otherwise falls back to legacy scoring.
   */
  public getPasswordStrength(): PasswordStrength {
    const policy = this._passwordPolicyService.getCachedPolicy();
    return getPasswordStrength(this.password, policy);
  }

  /**
   * Determines the appropriate CSS color class based on the password strength.
   * @returns CSS class name for styling the password strength indicator
   */
  public getPasswordStrengthColor(): string {
    const strength = this.getPasswordStrength();
    switch (strength) {
      case 'weak':
        return 'text-signal-error';
      case 'medium':
        return 'text-signal-success';
      case 'strong':
        return 'text-signal-success';
      default:
        return 'text-gray-600';
    }
  }

  /**
   * Gets the localized text representation of the password strength.
   * Uses the translation service to return user-friendly strength labels.
   * @returns Translated text describing the password strength level
   */
  public getPasswordStrengthText(): string {
    const strength = this.getPasswordStrength();
    switch (strength) {
      case 'weak':
        return this._translocoService.translate('components.passwordStrength.weak');
      case 'medium':
        return this._translocoService.translate('components.passwordStrength.strong');
      case 'strong':
        return this._translocoService.translate('components.passwordStrength.super');
      default:
        return '';
    }
  }
}
